import type { SwitchingRead } from '@gridcon-energy/schaltungstool-backend'
import { injectSBlockRestrictionsRepository } from '~/composables/repositories/switchingBlockRestrictionsRepository'
import { inject, ref } from 'vue'
import type { Ref } from 'vue'
import { toggleInSet } from '~/helper'
import { injectSwitchingBlocksRepository } from '~/composables/repositories/switchingBlocksRepository'
import { injectSwitchingsRepository } from '~/composables/repositories/switchingsRepository'

export enum UnlockBarsScopeEnum {
  BAR = 'bar', // move bar independently of block
  SWITCHING_BLOCK = 'switching_block', // move block independently of related block
  SWITCHING_BLOCK_RELATED = 'switching_block_related', // just move everything but without limits
}

export const UnlockStatusIcons = {
  unlocked: 'mdi-lock-open-alert',
  locked: 'mdi-lock',
}
export const UnlockBarsScopeColor: Record<UnlockBarsScopeEnum | 'off', string> = {
  [UnlockBarsScopeEnum.BAR]: '#f700ff',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK]: '#dc3545',
  [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]: '#ffc107',
  off: '#808080',
}

const scopes: Record<UnlockBarsScopeEnum, Ref<Set<number>>> = {
  [UnlockBarsScopeEnum.BAR]: ref(new Set<number>()), // switching ids
  [UnlockBarsScopeEnum.SWITCHING_BLOCK]: ref(new Set<number>()), // sb ids
  [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]: ref(new Set<number>()), // sb ids
}

function toggleLockStatusBar(switchingId: number) {
  return toggleInSet(scopes[UnlockBarsScopeEnum.BAR].value, switchingId)
}

function toggleLockStatusBlock(
  scope: UnlockBarsScopeEnum.SWITCHING_BLOCK | UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED,
  relatedSwitchingBlocks: Set<number>,
) {
  const scopeSet = scopes[scope]
  const contained = relatedSwitchingBlocks.intersection(scopeSet.value)
  if (contained.size > 0) {
    for (const switchingBlockId of contained) {
      scopeSet.value.delete(switchingBlockId)
    }
  } else {
    for (const switchingBlockId of relatedSwitchingBlocks) {
      scopeSet.value.add(switchingBlockId)
    }
  }
}

export function useSwitchingsLocked() {
  const SBlockRestrictionsRepository = injectSBlockRestrictionsRepository()
  const switchingBlockRepository = injectSwitchingBlocksRepository()
  const switchingsRepository = injectSwitchingsRepository()

  /**
   * Toggle the lock status of a switching.
   */
  function toggleLockStatus(scope: UnlockBarsScopeEnum, switching: SwitchingRead) {
    if (scope === UnlockBarsScopeEnum.SWITCHING_BLOCK) {
      return toggleLockStatusBlock(scope, new Set([switching.switching_block_id]))
    }
    if (scope === UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED) {
      const relatedSwitchingBlocks = SBlockRestrictionsRepository.getRelatedSwitchingBlocks(switching)
      return toggleLockStatusBlock(scope, new Set(relatedSwitchingBlocks))
    }
    if (scope === UnlockBarsScopeEnum.BAR) {
      return toggleLockStatusBar(switching.id)
    }
  }

  /**
   * Toggle the lock status globally
   */
  function toggleLockStatusGlobal(scope: UnlockBarsScopeEnum, forceState?: boolean) {
    const setOfIds: number[] = []
    if (scope === UnlockBarsScopeEnum.SWITCHING_BLOCK || scope === UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED) {
      setOfIds.push(...switchingBlockRepository.switchingBlocksMap.value.keys())
    }
    if (scope === UnlockBarsScopeEnum.BAR) {
      setOfIds.push(...switchingsRepository.switchingsMap.value.keys())
    }

    const activate = forceState ?? !(scopes[scope].value.size > 0)
    scopes[scope].value = new Set(activate ? setOfIds : [])
  }
  /**
   * Get the all "unlock" scopes for a switching.
   */
  function getUnlockScopeMap(switching: SwitchingRead): Record<UnlockBarsScopeEnum, boolean> {
    return {
      [UnlockBarsScopeEnum.BAR]: scopes[UnlockBarsScopeEnum.BAR].value.has(switching.id),
      [UnlockBarsScopeEnum.SWITCHING_BLOCK]: scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK].value.has(
        switching.switching_block_id,
      ),
      [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]: scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED].value.has(
        switching.switching_block_id,
      ),
    }
  }

  /**
   * Get the all "unlock" scopes for a switching.
   */
  function getUnlockScopeMapGlobal(): Record<UnlockBarsScopeEnum, boolean> {
    return {
      [UnlockBarsScopeEnum.BAR]: scopes[UnlockBarsScopeEnum.BAR].value.size > 0,
      [UnlockBarsScopeEnum.SWITCHING_BLOCK]: scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK].value.size > 0,
      [UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED]: scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED].value.size > 0,
    }
  }

  /**
   * Get the specific "unlock" scope for a switching.
   */
  function getUnlockScope(switching: SwitchingRead): null | UnlockBarsScopeEnum {
    // Order is important
    if (scopes[UnlockBarsScopeEnum.BAR].value.has(switching.id)) return UnlockBarsScopeEnum.BAR
    if (scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK].value.has(switching.switching_block_id))
      return UnlockBarsScopeEnum.SWITCHING_BLOCK
    if (scopes[UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED].value.has(switching.switching_block_id))
      return UnlockBarsScopeEnum.SWITCHING_BLOCK_RELATED
    return null
  }
  return {
    getUnlockScope: getUnlockScope,
    getUnlockScopeMap: getUnlockScopeMap,
    getUnlockScopeMapGlobal: getUnlockScopeMapGlobal,
    toggleLockStatus: toggleLockStatus,
    toggleLockStatusGlobal: toggleLockStatusGlobal,
  }
}
